'use client';

import type {
  ColumnOrderState,
  ColumnSizingState,
  ColumnSort,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';

import {
  type TableSettings,
  tableSettingsStore,
} from '@campaign-manager/core/lib/storage/tableSettingsStore';

export interface UseTableSettingsOptions {
  defaultOrderBy?: ColumnSort;
  manualSorting?: boolean;
  /**
   * When false, IndexedDB is bypassed entirely — neither load on mount
   * nor save on change. Use when an outer mechanism (e.g. a saved
   * dashboard view payload) owns the state and per-tableId persistence
   * would leak across contexts. Defaults to true.
   */
  enabled?: boolean;
}

export interface UseTableSettingsResult {
  columnOrder: ColumnOrderState;
  setColumnOrder: Dispatch<SetStateAction<ColumnOrderState>>;
  columnVisibility: VisibilityState;
  setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>;
  columnSizing: ColumnSizingState;
  setColumnSizing: Dispatch<SetStateAction<ColumnSizingState>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  hasLoadedSettings: boolean;
}

/**
 * Persists per-table UI state (column order, visibility, sizing, sorting) to IndexedDB
 * via tableSettingsStore. Loads on mount and saves with a 500ms debounce.
 */
export function useTableSettings(
  tableId: string,
  options: UseTableSettingsOptions = {},
): UseTableSettingsResult {
  const { defaultOrderBy, manualSorting, enabled = true } = options;

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnSizing, setColumnSizingRaw] = useState<ColumnSizingState>({});
  const setColumnSizing = useCallback<Dispatch<SetStateAction<ColumnSizingState>>>(
    (action) => {
      setColumnSizingRaw((prev) => {
        const next = typeof action === 'function' ? action(prev) : action;
        return next;
      });
    },
    [tableId],
  );
  const [sorting, setSorting] = useState<SortingState>(defaultOrderBy ? [defaultOrderBy] : []);
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);

  useEffect(() => {
    if (!enabled) {
      // Persistence opted out — caller owns the state. Mark as loaded
      // so any consumers waiting on `hasLoadedSettings` move on.
      setHasLoadedSettings(true);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const saved = await tableSettingsStore.getSettings(tableId);
        if (cancelled) return;
        if (saved) {
          if (saved.columnOrder) setColumnOrder(saved.columnOrder);
          if (saved.columnVisibility) setColumnVisibility(saved.columnVisibility);
          if (saved.columnSizing) setColumnSizing(saved.columnSizing);
          if (saved.sorting && !manualSorting) setSorting(saved.sorting);
        }
      } catch (error) {
        console.error('Failed to load table settings:', error);
      } finally {
        if (!cancelled) setHasLoadedSettings(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tableId, manualSorting, enabled]);

  useEffect(() => {
    if (!enabled || !hasLoadedSettings) return;

    const timeoutId = setTimeout(() => {
      const next: TableSettings = {
        tableId,
        columnOrder,
        columnVisibility,
        columnSizing,
        sorting: manualSorting ? undefined : sorting,
      };
      tableSettingsStore.saveSettings(next).catch((error) => {
        console.error('Failed to save table settings:', error);
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    tableId,
    columnOrder,
    columnVisibility,
    columnSizing,
    sorting,
    manualSorting,
    hasLoadedSettings,
    enabled,
  ]);

  return {
    columnOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
    sorting,
    setSorting,
    hasLoadedSettings,
  };
}
